//@ts-nocheck
import React, { useEffect } from 'react';
import { useProjectTypes } from '@/hooks/api/useProjects';
import { ProjectType } from '@/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FolderKanban, Plus, Zap, LayoutTemplate } from 'lucide-react';
import { ProjectTypeDetails } from './tabs/DetailsTab';
import { useDialogManager } from '@/hooks/useDialogManager';
import { cn } from '@/lib/utils';

export const ProjectTypeSettings = () => {
  const { projectTypes, isLoading } = useProjectTypes();
  const { openDialog } = useDialogManager();

  const [selectedProjectType, setSelectedProjectType] =
    React.useState<ProjectType | null>(null);

  // Sync selection if data updates
  useEffect(() => {
    if (selectedProjectType && projectTypes) {
      const updatedSelectedItem = projectTypes.find(
        (pt) => pt.id === selectedProjectType.id,
      );
      if (updatedSelectedItem) {
        setSelectedProjectType(updatedSelectedItem);
      }
    }
  }, [projectTypes, selectedProjectType]);

  const handleSelect = (projectType: ProjectType) => {
    setSelectedProjectType(projectType);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start h-full">
      <div className="md:col-span-1">
        <Card className="border-border shadow-sm overflow-hidden h-full max-h-[calc(100vh-12rem)] flex flex-col">
          <CardHeader className="px-4 py-3 border-b border-border/50 bg-muted/30 flex flex-row items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4 text-muted-foreground text-secondary" />
              <CardTitle className="text-md font-semibold text-foreground">
                Project Types
              </CardTitle>
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10"
              onClick={() => openDialog('create-project-type')}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </CardHeader>

          <CardContent className="p-2 overflow-y-auto flex-1">
            <div className="space-y-1">
              {isLoading
                ? [1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-3">
                      <Skeleton className="h-4 w-4 rounded-sm" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  ))
                : projectTypes?.map((pt) => {
                    const isActive = selectedProjectType?.id === pt.id;

                    return (
                      <button
                        key={pt.id}
                        onClick={() => handleSelect(pt)}
                        className={cn(
                          'w-full text-left flex items-center justify-between px-3 py-3 text-sm font-medium transition-all rounded-r-md group cursor-pointer',
                          'border-l-[3px]',
                          isActive
                            ? 'border-l-secondary bg-primary/5 text-foreground'
                            : 'border-l-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <FolderKanban
                            className={cn(
                              'h-4 w-4 transition-colors',
                              isActive
                                ? 'text-secondary'
                                : 'text-muted-foreground group-hover:text-foreground',
                            )}
                          />
                          <span className="truncate">{pt.name}</span>
                        </div>
                      </button>
                    );
                  })}

              {!isLoading && projectTypes?.length === 0 && (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  No project types found.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <main className="md:col-span-2">
        {selectedProjectType ? (
          <ProjectTypeDetails
            key={selectedProjectType.id}
            projectType={selectedProjectType}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-[400px] rounded-lg border-2 border-dashed border-border bg-muted/10 animate-in fade-in zoom-in-95 duration-300">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Zap className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground">
              No Selection
            </h3>
            <p className="mb-4 text-center text-sm text-muted-foreground max-w-xs">
              Select a project type from the list to view details or add a new
              type.
            </p>
            <Button
              variant="outline"
              onClick={() => openDialog('create-project-type')}
              className="gap-2"
            >
              <Plus className="h-4 w-4" /> Create Type
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};
